import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ShieldCheck } from "lucide-react";

/**
 * Blocking modal that appears whenever an authenticated user has not accepted
 * the current Terms of Use or Privacy Policy version. Checkboxes are NEVER
 * pre-checked. Marketing consent is separate and optional.
 * When a policy version is materially updated in the backend, existing users
 * are re-prompted automatically on next `/auth/me` refresh.
 */
export default function PolicyAcceptanceGate() {
  const { user, checkAuth } = useAuth();
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);

  const status = user?.policy_status;
  const open = !!(user && status?.needs_acceptance);
  const missing = status?.missing || [];
  const isReAccept =
    open && missing.length < 2; // user had accepted at least one before

  useEffect(() => {
    // Reset checkbox state whenever gate re-opens so a change of version
    // never carries stale accepted-state.
    if (open) {
      setTerms(false);
      setPrivacy(false);
      setMarketing(false);
    }
  }, [open]);

  const submit = async () => {
    if (!terms || !privacy) return;
    setBusy(true);
    try {
      await api.post("/policies/accept", {
        terms: true,
        privacy: true,
        // Only send true when the user affirmatively opted in.
        marketing: marketing ? true : undefined,
      });
      if (typeof checkAuth === "function") await checkAuth();
      toast.success("Thanks — your acceptance was recorded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record your acceptance");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-lg rounded-none [&>button]:hidden"
        // Non-dismissible: no close via ESC / outside click / X button
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="policy-acceptance-dialog"
      >
        <DialogHeader>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-100 text-slate-800 mb-2">
            <ShieldCheck strokeWidth={1.75} className="w-6 h-6" />
          </div>
          <DialogTitle className="font-serif-display text-2xl">
            {isReAccept ? "Please review our updated policies" : "Before you continue"}
          </DialogTitle>
          <DialogDescription className="text-slate-700 text-sm mt-2">
            {isReAccept
              ? "We have updated the documents below. Please review and accept the current versions to keep using ProSe.Claims."
              : "To use ProSe.Claims you must affirmatively accept the Terms of Use and acknowledge the Privacy Policy."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <label className="flex items-start gap-3 border border-slate-200 p-3 cursor-pointer">
            <Checkbox
              checked={terms}
              onCheckedChange={(v) => setTerms(!!v)}
              className="mt-0.5"
              data-testid="policy-terms-checkbox"
            />
            <div className="text-sm text-slate-800 leading-relaxed">
              I have read and agree to the
              {" "}
              <Link to="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-4 font-medium">
                Terms of Use
              </Link>
              .
            </div>
          </label>

          <label className="flex items-start gap-3 border border-slate-200 p-3 cursor-pointer">
            <Checkbox
              checked={privacy}
              onCheckedChange={(v) => setPrivacy(!!v)}
              className="mt-0.5"
              data-testid="policy-privacy-checkbox"
            />
            <div className="text-sm text-slate-800 leading-relaxed">
              I acknowledge that I have read the
              {" "}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-4 font-medium">
                Privacy Policy
              </Link>
              .
            </div>
          </label>

          <label className="flex items-start gap-3 border border-dashed border-slate-300 p-3 cursor-pointer" data-testid="policy-marketing-block">
            <Checkbox
              checked={marketing}
              onCheckedChange={(v) => setMarketing(!!v)}
              className="mt-0.5"
              data-testid="policy-marketing-checkbox"
            />
            <div className="text-sm text-slate-700 leading-relaxed">
              <span className="label-tiny">Optional</span>
              <div>Send me occasional product updates and educational content. You can unsubscribe at any time.</div>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button
            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
            disabled={!terms || !privacy || busy}
            onClick={submit}
            data-testid="policy-accept-btn"
          >
            {busy ? "Saving…" : "Accept and continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
